/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import {
  AvailableChargingSession,
  ChargingSessionFilters,
  ChargingSessionInitialProperties,
  ChargingSessionRequest,
  ChargingSessionResponse,
} from '../../interface/charging-session.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import chargingSessionApi from '../../utils/api/charging-session';

// a thunk to fetch devices
export const fetchChargingSessions = createAsyncThunk<
  ChargingSessionResponse,
  ChargingSessionRequest
>('charging-session/fetchChargingSessions', async (data: ChargingSessionRequest, { dispatch }) => {
  try {
    const response = await chargingSessionApi.fetchDevices(removeEmptyKeys(data));
    return {
      ...response.data,
      results: response.data.results.map((sessionData: AvailableChargingSession) => ({
        ...sessionData,
        date: ConvertUTCtoDate(sessionData.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(sessionData.createdAt)),
        transactionStartsAt: `${ConvertUTCtoDate(sessionData.transactionStartsAt)} - ${FormatUtcString(ConvertUTCtoHHMM(sessionData.transactionStartsAt))}`,
        transactionEndsAt: `${ConvertUTCtoDate(sessionData.transactionEndsAt)} - ${FormatUtcString(ConvertUTCtoHHMM(sessionData.transactionEndsAt))}`,
        transactionId: sessionData.transactionId,
        id: sessionData._id,
      })),
    };
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'charging-session/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await chargingSessionApi.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ChargingSessionInitialProperties = {
  chargingSessions: [],
  loading: false,
  page: DefaultApiLimit.page,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const chargingSessionSlice = createSlice({
  name: 'charging-session',
  initialState,
  reducers: {
    /**
     * Set User filter data function
     * @param state
     * @param action
     */
    setChargingSessionFilterData: (state, action: PayloadAction<ChargingSessionFilters>) => {
      state.filter = action.payload;
    },

    /**
     * Function to reset charging session to initial state
     * @param state
     */
    resetChargingSessionInitialState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update station management filters
     * @param state
     * @param action
     */
    updateChargingFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },

    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },

  /**
   * Extra reducers to handle thunk request
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchChargingSessions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchChargingSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.chargingSessions = action.payload.results;
      })
      .addCase(fetchChargingSessions.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setChargingSessionFilterData,
  resetChargingSessionInitialState,
  updateChargingFilters,
  setSortData,
} = chargingSessionSlice.actions;

// Export the reducer
export default chargingSessionSlice.reducer;
